import Backbone from 'backbone';
import $ from 'jquery';

import {AnalyticsEventMetadata} from 'analytics/AnalyticsTypes';
import {assertAndContinue} from 'Asserts';
import {X_BENCH_ANALYTICS_METADATA} from 'constants/AnalyticsConstants';
import * as csrf from 'CSRF';
import lazyInit from 'util/lazyInit';
import {FailedTaskMetadata} from 'util/TaskRunner';

// This header is a means for any API to return warnings to the user.
const WARNINGS_HEADER = 'X-Bnch-Warnings';

type BenchlingJQueryAjaxSettings = JQueryAjaxSettings & {untracked: boolean};

export interface HTTPHeaders {
  [key: string]: string;
}

let currentDeferred: JQueryPromise<unknown> | null = null;

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
const events: any = {...Backbone.Events};

const getHeaderGenerators = lazyInit<Array<{(): HTTPHeaders}>>(() => []);

/**
 * Construct headers for an XHR using all registered header generators and the passed analytics metadata.
 *
 * @param analyticsMetadata Optional analytics metadata to include in the headers. Note that this overrides
 *        analytics metadata set by the getAnalyticsMetadataHeaders header generator, which relies on the
 *        deprecated analytics context.
 */
export function getXHRHeaders(analyticsMetadata?: AnalyticsEventMetadata): HTTPHeaders {
  const headers: HTTPHeaders = {};
  for (const generator of getHeaderGenerators()) {
    Object.assign(headers, generator());
  }
  if (analyticsMetadata != null) {
    headers[X_BENCH_ANALYTICS_METADATA] = encodeURIComponent(JSON.stringify(analyticsMetadata));
  }
  return headers;
}

export function registerHeaderGenerator(generator: () => HTTPHeaders): void {
  getHeaderGenerators().push(generator);
}

// Update args.headers definition in runTask in TaskRunner.ts if these diverge
const getPOSTHeaders = getXHRHeaders;
const getPATCHHeaders = getXHRHeaders;
const getPUTHeaders = getXHRHeaders;
const getDELETEHeaders = getXHRHeaders;

function submitCSRFForm(
  method: string,
  action: string,
  {target, datas = []}: {target?: string; datas?: Array<{[key: string]: unknown}>} = {}
): void {
  const $form = $('<form>').attr({method, action});
  if (target != null) {
    $form.prop('target', target);
  }
  for (const data of [{csrf_token: csrf.token}, ...datas]) {
    for (const name of Object.keys(data)) {
      const value = data[name];
      $form.append($('<input>').attr({type: 'hidden', name, value}));
    }
  }
  $form.hide().appendTo('body');
  $form.submit().remove();
}

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
function getJSONTracked(url: string, data?: object): JQueryPromise<any> {
  const xhr: JQueryXHR = $.getJSON(url, data);
  if (currentDeferred != null) {
    currentDeferred = currentDeferred.then(() => xhr);
  } else {
    currentDeferred = xhr;
    currentDeferred.always(() => {
      events.trigger('ajaxStop');
      currentDeferred = null;
    });
    events.trigger('ajaxStart');
  }
  return xhr;
}

/**
 * @deprecated - New callers should use getPromise instead.
 */
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
function get(url: string, data?: object): JQueryPromise<any> {
  return $.get(url, data);
}

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
function getPromise(url: string, data?: object): Promise<any> {
  return Promise.resolve(get(url, data));
}

/**
 * @deprecated - New callers should use getJSONPromise instead.
 */
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
function getJSON(url: string, data?: object): JQueryPromise<any> {
  return $.getJSON(url, data);
}

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
function getJSONPromise(url: string, data?: object): Promise<any> {
  return Promise.resolve(getJSON(url, data));
}

/**
 * @deprecated - New callers should use postPromise instead.
 */
function post({
  url,
  data,
  analyticsMetadata,
  extraHeaders,
}: {
  url: string;
  // oxlint-disable-next-line typescript/no-explicit-any
  data?: any;
  analyticsMetadata?: AnalyticsEventMetadata;
  extraHeaders?: HTTPHeaders;
}): // oxlint-disable-next-line @typescript-eslint/no-explicit-any
JQueryPromise<any> {
  return $.ajax({
    type: 'POST',
    url,
    data,
    dataType: 'json',
    headers: {...getPOSTHeaders(analyticsMetadata), ...extraHeaders},
  });
}

function postPromise({
  url,
  data,
  analyticsMetadata,
  extraHeaders,
}: {
  url: string;
  // oxlint-disable-next-line typescript/no-explicit-any
  data?: any;
  analyticsMetadata?: AnalyticsEventMetadata;
  extraHeaders?: HTTPHeaders;
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
}): Promise<any> {
  return Promise.resolve(post({url, data, analyticsMetadata, extraHeaders}));
}

/**
 * @deprecated - New callers should use postJSONPromise instead.
 */
function postJSON({
  url,
  data,
  untracked,
  analyticsMetadata,
  extraHeaders,
}: {
  url: string;
  // oxlint-disable-next-line typescript/no-explicit-any
  data?: any;
  untracked?: boolean;
  analyticsMetadata?: AnalyticsEventMetadata;
  extraHeaders?: HTTPHeaders;
}): // oxlint-disable-next-line @typescript-eslint/no-explicit-any
JQueryPromise<any> {
  if (untracked == null) {
    untracked = false;
  }
  return $.ajax({
    contentType: 'application/json',
    data: JSON.stringify(data),
    type: 'POST',
    dataType: 'json',
    url,
    untracked, // custom setting that is filtered out in XHRCounter
    headers: {...getPOSTHeaders(analyticsMetadata), ...extraHeaders},
  } as JQueryAjaxSettings);
}

function postJSONPromise({
  url,
  data,
  untracked,
  analyticsMetadata,
  extraHeaders,
}: {
  url: string;
  // oxlint-disable-next-line typescript/no-explicit-any
  data?: any;
  untracked?: boolean;
  analyticsMetadata?: AnalyticsEventMetadata;
  extraHeaders?: HTTPHeaders;
}): // oxlint-disable-next-line @typescript-eslint/no-explicit-any
Promise<any> {
  return Promise.resolve(postJSON({url, data, untracked, analyticsMetadata, extraHeaders}));
}

/**
 * @deprecated - New callers should use patchJSONPromise instead.
 */
function patchJSON({
  url,
  data,
  analyticsMetadata,
  extraHeaders,
}: {
  url: string;
  // oxlint-disable-next-line typescript/no-explicit-any
  data?: any;
  analyticsMetadata?: AnalyticsEventMetadata;
  extraHeaders?: HTTPHeaders;
}): // oxlint-disable-next-line @typescript-eslint/no-explicit-any
JQueryPromise<any> {
  return $.ajax({
    contentType: 'application/json',
    data: JSON.stringify(data),
    type: 'PATCH',
    dataType: 'json',
    url,
    headers: {...getPATCHHeaders(analyticsMetadata), ...extraHeaders},
  });
}

function patchJSONPromise({
  url,
  data,
  analyticsMetadata,
  extraHeaders,
}: {
  url: string;
  // oxlint-disable-next-line typescript/no-explicit-any
  data?: any;
  analyticsMetadata?: AnalyticsEventMetadata;
  extraHeaders?: HTTPHeaders;
}): // oxlint-disable-next-line @typescript-eslint/no-explicit-any
Promise<any> {
  return Promise.resolve(patchJSON({url, data, analyticsMetadata, extraHeaders}));
}

/**
 * @deprecated - New callers should use putJSONPromise instead.
 */
function putJSON({
  url,
  data,
  analyticsMetadata,
  extraHeaders,
}: {
  url: string;
  // oxlint-disable-next-line typescript/no-explicit-any
  data?: any;
  analyticsMetadata?: AnalyticsEventMetadata;
  extraHeaders?: HTTPHeaders;
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
}): JQueryPromise<any> {
  return $.ajax({
    contentType: 'application/json',
    data: JSON.stringify(data),
    type: 'PUT',
    dataType: 'json',
    url,
    headers: {...getPUTHeaders(analyticsMetadata), ...extraHeaders},
  });
}

function putJSONPromise({
  url,
  data,
  analyticsMetadata,
  extraHeaders,
}: {
  url: string;
  // oxlint-disable-next-line typescript/no-explicit-any
  data?: any;
  analyticsMetadata?: AnalyticsEventMetadata;
  extraHeaders?: HTTPHeaders;
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
}): Promise<any> {
  return Promise.resolve(putJSON({url, data, analyticsMetadata, extraHeaders}));
}

/**
 * @deprecated - New callers should use deletePromise instead.
 */
function delete_({
  url,
  data,
  analyticsMetadata,
  extraHeaders,
}: {
  url: string;
  // oxlint-disable-next-line typescript/no-explicit-any
  data?: any;
  analyticsMetadata?: AnalyticsEventMetadata;
  extraHeaders?: HTTPHeaders;
}): // oxlint-disable-next-line @typescript-eslint/no-explicit-any
JQueryPromise<any> {
  return $.ajax({
    contentType: 'application/json',
    data: JSON.stringify(data),
    type: 'DELETE',
    dataType: 'json',
    url,
    headers: {...getDELETEHeaders(analyticsMetadata), ...extraHeaders},
  });
}

function deletePromise({
  url,
  data,
  analyticsMetadata,
  extraHeaders,
}: {
  url: string;
  // oxlint-disable-next-line typescript/no-explicit-any
  data?: any;
  analyticsMetadata?: AnalyticsEventMetadata;
  extraHeaders?: HTTPHeaders;
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
}): Promise<any> {
  return Promise.resolve(delete_({url, data, analyticsMetadata, extraHeaders}));
}

function postFile({
  url,
  file,
  progress,
  analyticsMetadata,
  extraHeaders,
}: {
  url: string;
  file: string | Blob;
  progress: (arg: {lengthComputable: boolean; loaded?: number; total?: number}) => void;
  analyticsMetadata?: AnalyticsEventMetadata;
  extraHeaders?: HTTPHeaders;
}): // oxlint-disable-next-line @typescript-eslint/no-explicit-any
JQueryPromise<any> {
  return $.ajax({
    url,
    type: 'POST',
    dataType: 'json',
    headers: {...getPOSTHeaders(analyticsMetadata), ...extraHeaders},
  })
    .then((res: {action: string; fields: Array<{name: string; value: string}>}) => {
      const formData = new FormData();
      for (const field of Array.from(res.fields)) {
        formData.append(field.name, field.value);
      }
      formData.append('file', file);
      return $.ajax({
        url: res.action,
        data: formData,
        processData: false,
        contentType: false,
        type: 'POST',
        progress,
      } as JQueryAjaxSettings);
    })
    .then((data, text, xhr) => xhr.getResponseHeader('Location'));
}

function postFileAsFormData({
  url,
  key,
  file,
  analyticsMetadata,
  extraHeaders,
}: {
  url: string;
  key: string;
  file: string | Blob;
  analyticsMetadata?: AnalyticsEventMetadata;
  extraHeaders?: HTTPHeaders;
}): // oxlint-disable-next-line @typescript-eslint/no-explicit-any
JQueryPromise<any> {
  const formData = new FormData();
  formData.append(key, file);
  return $.ajax({
    url,
    data: formData,
    processData: false,
    contentType: false,
    type: 'POST',
    headers: {...getPOSTHeaders(analyticsMetadata), ...extraHeaders},
  });
}

function postFileAsFormDataPromise({
  url,
  key,
  file,
  analyticsMetadata,
  extraHeaders,
}: {
  url: string;
  key: string;
  file: string | Blob;
  analyticsMetadata?: AnalyticsEventMetadata;
  extraHeaders?: HTTPHeaders;
}): // oxlint-disable-next-line @typescript-eslint/no-explicit-any
Promise<any> {
  return Promise.resolve(postFileAsFormData({url, key, file, analyticsMetadata, extraHeaders}));
}

class XHRCounter {
  count: number;

  constructor() {
    this.count = 0;
  }

  start(): void {
    // Count XHRs that matter: POST, PUT, PATCH, and DELETE.
    const xhrFilter = (options: BenchlingJQueryAjaxSettings): boolean =>
      options.type!.toUpperCase() !== 'GET' && !options.untracked;

    $(document)
      .ajaxSend((e, xhr, options: BenchlingJQueryAjaxSettings) => {
        if (xhrFilter(options)) {
          this.count++;
        }
      })
      .ajaxComplete((e, xhr, options) => {
        if (xhrFilter(options)) {
          this.count--;
        }
      });
  }

  getCount(): number {
    return this.count;
  }
}

function isJqXHR(e: unknown): e is JQueryXHR {
  return e != null && (e as JQueryXHR).getAllResponseHeaders != null;
}

/**
 * Determine if this error is a network response. We need something like this to distinguish between
 * 400/500 errors thrown as part of async functions and other errors thrown from JavaScript.
 */
function isServerRequestError(e: unknown): boolean {
  const isTaskError = e != null && (e as FailedTaskMetadata).isBenchlingTaskError;
  return isJqXHR(e) || isTaskError;
}

/**
 * If the error is a server-provided error and it has a user-facing message associated with it, return
 * that message. Otherwise, return null.
 */
export function getUserMessage(e: unknown): string | null {
  if (!isServerRequestError(e)) {
    return null;
  }
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  return (e as any)?.responseJSON?.userMessage ?? (e as any)?.userMessage ?? null;
}

function isAbortError(e: unknown): boolean {
  return e instanceof window.DOMException && e.code === window.DOMException.ABORT_ERR;
}

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
function assertWarningHeaderIsArray(parsedWarnings: any): parsedWarnings is Array<any> {
  return assertAndContinue(
    Array.isArray(parsedWarnings),
    `${WARNINGS_HEADER} in API response was not an array`,
    {[WARNINGS_HEADER]: parsedWarnings}
  );
}

function getResponseWarnings(xhr: JQueryXHR): Array<string> {
  const warnings: Array<string> = [];
  const warningsHeader = xhr.getResponseHeader(WARNINGS_HEADER);
  if (warningsHeader) {
    try {
      const parsedWarnings = JSON.parse(warningsHeader);
      if (assertWarningHeaderIsArray(parsedWarnings)) {
        parsedWarnings.forEach((warning) => {
          if (
            assertAndContinue(typeof warning === 'string', `${WARNINGS_HEADER} element was not a string`, {
              warning,
            })
          ) {
            warnings.push(warning);
          }
        });
      }
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      // If the warning header is present but malformed, we don't want to show an error to the
      // user, so just log and swallow it.
      assertAndContinue(false, `Malformed ${WARNINGS_HEADER} header in API response`, {
        [WARNINGS_HEADER]: warningsHeader,
      });
    }
  }

  return warnings;
}

const xhrCounter = new XHRCounter();

export {
  submitCSRFForm,
  getJSONTracked,
  get,
  getPOSTHeaders,
  getPromise,
  getJSON,
  getJSONPromise,
  isJqXHR,
  patchJSON,
  patchJSONPromise,
  post,
  postPromise,
  postJSON,
  postJSONPromise,
  postFile,
  putJSON,
  putJSONPromise,
  delete_,
  deletePromise,
  postFileAsFormData,
  postFileAsFormDataPromise,
  isServerRequestError,
  isAbortError,
  xhrCounter,
  events,
  getResponseWarnings,
  WARNINGS_HEADER,
};
